/**
 * ================================================================
 * AUTH GUARD - Route Protection Component
 * ================================================================
 *
 * Component bảo vệ routes theo Better Auth standard
 *
 * Features:
 * - Kiểm tra authentication status từ Better Auth session
 * - Validate state data cho auth flow
 * - Redirect logic cho protected routes
 * - State validation cho OTP flows
 *
 * @author MeBi Team
 * @version 2.1.0 (Better Auth Standard)
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getCurrentRole } from "@/stores/authStore";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useCurrentUser } from "@/features/auth/hooks/useSession";

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireNoAuth?: boolean;
  requireStateData?: boolean;
  allowedFromPaths?: string[];
}

const canBypassRequireNoAuth = (
  pathname: string,
  state: AuthState | null,
): boolean => {
  if (!state?.fromChangePassword) {
    return false;
  }

  return (
    pathname === "/forgot-password" || pathname === "/forgot-password-confirm"
  );
};

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = false,
  requireNoAuth = false,
  requireStateData = false,
  allowedFromPaths = [],
}) => {
  const location = useLocation();
  const state = (location.state as AuthState | null) ?? null;
  const { isAuthenticated, isLoading: sessionLoading } = useCurrentUser();

  if (sessionLoading) {
    return <LoadingSpinner text="Đang tải..." />;
  }

  // Require authentication (protected routes)
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Require no authentication (auth pages)
  if (
    requireNoAuth &&
    isAuthenticated &&
    !canBypassRequireNoAuth(location.pathname, state)
  ) {
    return <Navigate to={getRoleRedirectRoute()} replace />;
  }

  // Require state data validation
  if (
    requireStateData &&
    !validateStateData(location.pathname, state, allowedFromPaths)
  ) {
    return <Navigate to={getRedirectPath(location.pathname)} replace />;
  }

  return <>{children}</>;
};

// Helper to determine redirect route based on role
const getRoleRedirectRoute = () => {
  const savedRole = getCurrentRole();
  const roleRoutes: Record<string, string> = {
    Player: "/tabs/home",
    Organizer: "/organizer-tabs/home",
  };

  return (savedRole && roleRoutes[savedRole]) || "/redirect-role";
};

interface AuthState {
  phone?: string;
  fromRegistration?: boolean;
  fromRegister?: boolean;
  fromForgotPassword?: boolean;
  fromChangePassword?: boolean;
  fromProfileVerification?: boolean;
  method?: string;
  purpose?: string;
  contactInfo?: string;
  returnToPath?: string;
  otp_token?: string;
  token_hash?: string;
  [key: string]: unknown;
}

// Helper functions to reduce Cognitive Complexity
const validateStateData = (
  pathname: string,
  state: AuthState | null,
  allowedFromPaths: string[],
): boolean => {
  console.log(`[AuthGuard] Validating state for ${pathname}`, {
    state,
    allowedFromPaths,
  });

  if (!state) {
    console.warn(`[AuthGuard] State is missing for ${pathname}`);
    return false;
  }

  // Check if current requirements match the state
  const checks = [
    allowedFromPaths.includes("/register") &&
      (Boolean(state.phone) ||
        Boolean(state.fromRegistration) ||
        Boolean(state.fromRegister)),
    allowedFromPaths.includes("/forgot-password") &&
      (Boolean(state.phone) || Boolean(state.fromForgotPassword)),
    allowedFromPaths.includes("/verify-otp-method") &&
      Boolean(state.phone) &&
      (Boolean(state.method) || Boolean(state.contactInfo)),
    allowedFromPaths.includes("/verify-otp") && Boolean(state.phone),
    allowedFromPaths.includes("/update-profile") &&
      Boolean(state.phone) &&
      (state.purpose === "sign-in" || Boolean(state.fromProfileVerification)),
    pathname === "/reset-password" &&
      Boolean(state.phone) &&
      Boolean(state.token_hash),
  ];

  const isValid = checks.some(Boolean);
  if (!isValid) {
    console.warn(`[AuthGuard] State validation failed for ${pathname}`, {
      checks,
      stateKeys: Object.keys(state),
    });
  }
  return isValid;
};

const getRedirectPath = (pathname: string): string => {
  switch (pathname) {
    case "/verify-otp-method":
    case "/verify-otp":
      return "/login";
    case "/reset-password":
      return "/forgot-password";
    default:
      return "/login";
  }
};

export default AuthGuard;
